<script setup lang="ts">
import config from "~/config.json";
import Card from "~/components/ui/Card.vue";
import UiPagination from "~/components/ui/Pagination.vue";
import arrayChunks from "~/utils/arrayChunks";
import { ref, computed } from "vue";

export type Review = {
  source: string;
  rating: number;
  text: string;
  author: string;
};

const props = defineProps({
  maxRowsPerPage: {
    type: Number,
    default: 1,
  },
  pagination: {
    type: Boolean,
    default: true,
  },
});

const currentPage = ref<number>(0);
const reviewsPerPage = computed<Review[][][]>(() => {
  const items = arrayChunks(config.reviews, 3) || [];

  return arrayChunks(items, props.maxRowsPerPage || 1) || [];
});
const totalPages = computed<number>(() => reviewsPerPage.value.length);
const currentReviews = computed<Review[]>(() => {
  const page = reviewsPerPage.value[currentPage.value] || [];

  return page.flat();
});
</script>

<template>
  <div class="reviews">
    <div class="cards" v-if="currentReviews.length > 0">
      <Card class="card" v-for="(review, reviewIndex) in currentReviews" :key="reviewIndex">
        <div class="head">
          <div class="author">
            <div class="source">
              <Icon class="google" name="simple-icons:google" v-if="review.source === 'google'" />
            </div>

            {{ review.author || "Anonymní zákazník" }}
          </div>

          <div class="rating">
            Hodnocení:
            <div class="stars">
              <Icon name="material-symbols:star-rounded" class="star" v-for="starIndex in review.rating" :key="starIndex" />
            </div>
          </div>
        </div>

        <p class="review" v-if="review.text">
          {{ review.text }}
        </p>

        <p class="review italic" v-else>
          Recenze nemá žádný text.
        </p>
      </Card>
    </div>

    <div v-else>
      <p class="no-reviews">Žádné recenze k zobrazení.</p>
    </div>

    <UiPagination v-model="currentPage" :totalPages="totalPages" v-if="pagination && currentReviews.length > 0 && totalPages > 1" />
  </div>
</template>

<style scoped lang="scss">
.reviews {
  display: flex;
  flex-direction: column;
  gap: 30px;

  .no-reviews {
    font-size: 18px;
    color: rgb(var(--text-light-color));
  }

  .cards {
    column-count: 3;
    column-gap: 30px;

    .card {
      display: flex;
      margin: 0 0 30px;
      break-inside: avoid;
      -webkit-column-break-inside: avoid;
      page-break-inside: avoid;
      flex-direction: column;
      gap: 20px;
      padding: 30px;
      min-width: 250px;
      border-radius: var(--large-border-radius);
      color: rgb(var(--text-light-color));

      .head {
        display: flex;
        flex-direction: row;
        gap: 15px;
        align-items: center;
        justify-content: space-between;

        .author {
          display: flex;
          align-items: center;
          gap: 15px;
          font-size: 18px;
          font-weight: 600;
          color: rgb(var(--title-light-color));

          .source {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 40px;
            min-height: 40px;
            border-radius: 50%;
            background: rgba(var(--mini-title-light-color), 0.3);

            .google {
              transition: 0.2s;
              color: rgb(var(--title-dark-color));
              font-size: 24px;
              line-height: 0;
            }
          }
        }

        .rating {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          font-size: 15px;
          gap: 10px;
          color: rgb(var(--text-light-color));

          .stars {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            margin-left: 5px;

            .star {
              transition: 0.2s;
              font-size: 18px;
              color: rgb(var(--main-color));
            }
          }
        }
      }

      .review {
        font-size: 18px;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;

        &.italic {
          font-style: italic;
          color: rgb(var(--mini-title-light-color));
        }
      }

      &:hover .head {
        .stars .star {
          color: rgb(var(--title-light-color));
        }

        .source .google {
          color: rgb(var(--title-light-color));
        }
      }
    }
  }
}

@media (max-width: 1100px) {
  .reviews .cards {
    column-count: 2;
  }
}
@media (max-width: 700px) {
  .reviews .cards {
    column-count: 1;
  }
}
</style>
